export interface AmountParseResult {
  value: number;
  consumedRaw: string;
}

export interface ExtractAmountResult {
  amountResult: AmountParseResult;
  textWithPlaceholder: string;
}

/**
 * Regex patterns for amount extraction. Each requires an explicit currency signal (€ or "euro").
 * Decimal separator: comma or dot, up to 2 decimal places.
 *
 * Patterns are tested in order; first match wins.
 */
const AMOUNT_PATTERNS: RegExp[] = [
  // €50 / € 50 / €30,50 / € 30.50
  /€\s*(\d+(?:[.,]\d{1,2})?)/,
  // 50€ / 30,50€
  /(\d+(?:[.,]\d{1,2})?)\s*€/,
  // 50 euro / 30,50 euro
  /(\d+(?:[.,]\d{1,2})?)\s+euro\b/,
];

function parseAmount(raw: string): number {
  return parseFloat(raw.replace(',', '.'));
}

/**
 * Extracts an amount from text that has already been processed by extractDates
 * (date segments replaced with __DATE__). Requires explicit currency signal.
 *
 * Returns null if no amount found or amount is invalid (<= 0).
 */
export function extractAmount(text: string): ExtractAmountResult | null {
  const lower = text.toLowerCase();

  for (const pattern of AMOUNT_PATTERNS) {
    const match = lower.match(pattern);
    if (!match) continue;

    const value = parseAmount(match[1]);
    if (!isFinite(value) || value <= 0) continue;

    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;

    // Replace matched segment in original text (preserve case outside match)
    const textWithPlaceholder =
      text.substring(0, matchStart).trimEnd() +
      ' __AMOUNT__ ' +
      text.substring(matchEnd).trimStart();

    return {
      amountResult: {
        value,
        consumedRaw: text.substring(matchStart, matchEnd).trim(),
      },
      textWithPlaceholder: textWithPlaceholder.replace(/\s+/g, ' ').trim(),
    };
  }

  return null;
}
